import React, { useCallback, useEffect, useMemo, useState } from 'react';
import config from '../config';
import { billingApi, reportApi } from '../services/api';
import Brand from './Brand';
import HeaderNav from './HeaderNav';
import StripeCheckoutModal from './StripeCheckoutModal';
import SubscriptionActivation from './SubscriptionActivation';
import PlanDowngradeDialog from './PlanDowngradeDialog';
import { planLabel as getPlanLabel } from '../utils/planLabels';
import { HUB_DESCRIPTIONS } from '../utils/hubDescriptions';

const list = (value) => (Array.isArray(value) ? value : []);
const PLAN_ORDER = ['client', 'tier_0', 'tier_1', 'tier_2', 'tier_3'];
const planLabel = (plan) => getPlanLabel(plan.id, plan.label);
// Stripe's charge units differ from ISO currency digits for ISK and UGX.
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga',
  'pyg', 'rwf', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'
]);
const THREE_DECIMAL_CURRENCIES = new Set(['bhd', 'jod', 'kwd', 'omr', 'tnd']);
const stripePriceLabel = (price) => {
  const currency = price.currency.toLowerCase();
  const zeroDecimal = ZERO_DECIMAL_CURRENCIES.has(currency);
  const threeDecimal = THREE_DECIMAL_CURRENCIES.has(currency);
  const amount = price.unit_amount / (zeroDecimal ? 1 : threeDecimal ? 1000 : 100);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: price.currency,
    currencyDisplay: 'code',
    minimumFractionDigits: zeroDecimal ? 0 : threeDecimal ? 3 : 2,
    maximumFractionDigits: zeroDecimal ? 0 : threeDecimal ? 3 : 2
  }).format(amount);
  return `${formatted} / ${price.interval === 'annual' ? 'year' : 'month'}`;
};
const valueLabel = (value) => {
  if (value == null) return 'Unlimited';
  if (typeof value === 'boolean') return value ? 'Included' : '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'object') return Object.entries(value).map(([key, item]) => `${key}: ${valueLabel(item)}`).join(' · ');
  return String(value);
};
const PLAN_LIMIT_ROWS = [
  ['company_reports_per_month', 'First Look reports'],
  ['full_reports', 'Deep Dive selections']
];
const FEATURE_DESCRIPTIONS = {
  company_reports_per_month: 'Your monthly allocation of First Look-only companies. Deep Dive includes First Look for free; upgrading returns that company\'s First Look selection for the month.',
  full_reports: 'Companies you select for deeper research, with all Deep Dive tabs included except restricted Report Version History. Each Deep Dive also includes that company\'s First Look without using your monthly First Look allowance.'
};
// The Free Tier's single company is named next to its count: "1 (TSLA)".
const FREE_TICKER_ROWS = new Set(['company_reports_per_month', 'full_reports']);
const HUB_SCOPE_LABELS = {
  free: 'Included Company',
  tracked: 'Tracked Companies',
  tracked_included: 'Included Company',
  all: 'Full Roster',
  showcase: 'Showcase Companies',
  mine: 'Held & Showcase Companies',
  halo: 'Held Companies & Related Groups',
  teaser: 'Market Summary'
};

const planLimit = (plan, key) => {
  const limits = plan.limits || {};
  if (key === 'company_reports_per_month') {
    for (const alias of ['company_reports_per_month', 'firstlook_picks_per_month', 'firstlook_picks_per_period']) {
      if (Object.prototype.hasOwnProperty.call(limits, alias)) return limits[alias];
    }
    return undefined;
  }
  return limits[key];
};

const planLimitLabel = (plan, key) => {
  const limit = planLimit(plan, key);
  const label = valueLabel(limit);
  if (Number.isInteger(plan.included_company_count) && FREE_TICKER_ROWS.has(key)) {
    return <>{label}{' '}<small className="plan-hub-scope">+ ({plan.included_company_count} Included Companies)</small></>;
  }
  return plan.free_ticker && FREE_TICKER_ROWS.has(key) ? `${label} (${plan.free_ticker})` : label;
};

const hubAccessLabel = (plan, hubId) => {
  const access = plan.hub_scopes?.[hubId] || 'none';
  if (access === 'none') return '—';
  const scope = HUB_SCOPE_LABELS[access];
  return scope || valueLabel(access);
};

const PlansPage = ({ user, onBack, onLogout, onOpenProfile, onRefresh }) => {
  const [paymentComplete, setPaymentComplete] = useState(() => new URLSearchParams(window.location.search).get('checkout') === 'complete' ? 1 : 0);
  const completeCheckout = useCallback(() => { setSubscription(null); setPaymentComplete(value => value + 1); }, []);
  const [catalog, setCatalog] = useState({ plans: [], hubs: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutPlans, setCheckoutPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [planPrices, setPlanPrices] = useState({});
  const [stripeConfigured, setStripeConfigured] = useState(false);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [pricesError, setPricesError] = useState('');
  const [downgrade, setDowngrade] = useState(null);
  const [pendingChange, setPendingChange] = useState(null);
  const [subscriptionPlan, setSubscriptionPlan] = useState(undefined);
  const [subscriptionStatusLoading, setSubscriptionStatusLoading] = useState(false);
  const [subscriptionStatusError, setSubscriptionStatusError] = useState('');
  const paidAssignment = ['tier_1', 'tier_2', 'tier_3'].includes(user?.plan);
  const billingPlan = paidAssignment && subscriptionPlan !== undefined ? subscriptionPlan : user?.plan;
  const currentPlan = billingPlan ?? user?.plan;
  const hasPaidPlan = ['tier_1', 'tier_2', 'tier_3'].includes(billingPlan);
  const cellClass = (plan) => plan.id === currentPlan ? 'plan-current' : undefined;
  const actionLabel = (plan) => !hasPaidPlan ? 'Subscribe'
    : PLAN_ORDER.indexOf(plan.id) > PLAN_ORDER.indexOf(billingPlan) ? 'Upgrade' : 'Downgrade';

  useEffect(() => {
    setPendingChange(null);
    setSubscriptionPlan(undefined);
    setSubscriptionStatusError('');
    setSubscriptionStatusLoading(paidAssignment);
    if (!paidAssignment) return undefined;
    let active = true;
    billingApi.me().then(result => {
      if (active) {
        setPendingChange(result.pending_change?.status === 'scheduled' ? result.pending_change : null);
        if (Object.prototype.hasOwnProperty.call(result, 'subscription_plan')) setSubscriptionPlan(result.subscription_plan);
      }
    }).catch(err => { if (active) setSubscriptionStatusError(err.message); })
      .finally(() => { if (active) setSubscriptionStatusLoading(false); });
    return () => { active = false; };
  }, [paidAssignment, user]);

  useEffect(() => {
    let active = true;
    billingApi.prices()
      .then((result) => {
        if (active) {
          setPlanPrices(result.prices);
          setStripeConfigured(result.stripe_configured !== false && Boolean(config.stripePublishableKey));
        }
      })
      .catch((err) => { if (active) setPricesError(err.message); })
      .finally(() => { if (active) setPricesLoading(false); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    setCheckoutPlans([]);
    setCheckoutError('');
    if (!user) return undefined;
    if (!config.stripePublishableKey) {
      return undefined;
    }
    let active = true;
    billingApi.checkoutOptions()
      .then((result) => {
        if (!active) return;
        const availablePlans = list(result.plans);
        setCheckoutPlans(availablePlans);
      })
      .catch((err) => { if (active) setCheckoutError(err.message); });
    return () => { active = false; };
  }, [user]);

  useEffect(() => {
    (user ? reportApi.plans() : reportApi.publicPlans())
      .then((result) => setCatalog(result))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [user]);

  // The server decides who sees the Client plan; Employee is never listed.
  const plans = useMemo(() => list(catalog.plans)
    .filter((plan) => plan.id !== 'employee')
    .sort((a, b) => {
      const aOrder = PLAN_ORDER.indexOf(a.id);
      const bOrder = PLAN_ORDER.indexOf(b.id);
      if (aOrder !== -1 || bOrder !== -1) {
        if (aOrder === -1) return 1;
        if (bOrder === -1) return -1;
        return aOrder - bOrder;
      }
      return (a.rank ?? 0) - (b.rank ?? 0);
    }), [catalog.plans]);
  const hubRows = list(catalog.hubs).filter((hub) =>
    !['contest', 'marketcap', 'pulse', 'discover'].includes(hub.id) && !hub.employee_only
  );
  const renderPriceRow = () => (
    <div className="plans-row plans-footer">
      <div><strong>Price</strong><p className="plan-hub-description">Taxes, if applicable, calculated at checkout.</p></div>
      {plans.map((plan) => {
        const monthlyPrices = list(planPrices[plan.id]).filter((price) => price.interval === 'monthly');
        const available = stripeConfigured && !pricesError && (plan.id === 'tier_0'
          || (monthlyPrices.length > 0 && (!user || checkoutPlans.some((option) => option.id === plan.id && list(option.intervals).includes('monthly')))));
        return (
          <div className={cellClass(plan)} key={`${plan.id}-price`}>
            {plan.id === 'client' ? 'Included with client access' : (
              pricesLoading ? 'Loading prices...' : pricesError ? 'Price unavailable' : (
                !available ? <strong>Coming soon</strong> : plan.id === 'tier_0' ? 'Free' : monthlyPrices.map((price) => (
                  <p key={price.interval}>
                    <strong>{stripePriceLabel(price)}</strong>
                    {price.tax_behavior === 'inclusive' && <small className="plan-hub-description"> (tax included)</small>}
                  </p>
                ))
              )
            )}
            {plan.id === 'tier_0' && !user && available && (
              <button className="primary-button plan-subscribe" type="button" onClick={() => setSubscription({ id: 'tier_0', label: 'Free Tier' })}>Sign up</button>
            )}
            {plan.id === currentPlan && <strong className="plan-current-label">Your current plan</strong>}
            {plan.id === 'tier_0' && hasPaidPlan && <button className="primary-button plan-subscribe" type="button" disabled={Boolean(pendingChange) || subscriptionStatusLoading || Boolean(subscriptionStatusError)} onClick={() => setDowngrade({ id: plan.id, label: planLabel(plan) })} aria-label="Downgrade to Free Tier">Downgrade</button>}
            {available && ['tier_1', 'tier_2', 'tier_3'].includes(plan.id) && plan.id !== billingPlan && (
              <button
                className="primary-button plan-subscribe"
                type="button"
                aria-label={`${actionLabel(plan)} to ${planLabel(plan)}`}
                disabled={Boolean(pendingChange) || subscriptionStatusLoading || Boolean(subscriptionStatusError) || pricesLoading || Boolean(pricesError)
                  || !monthlyPrices.length
                  || Boolean(user && !checkoutPlans.some((option) => option.id === plan.id && list(option.intervals).includes('monthly')))}
                onClick={() => {
                  const selected = { id: plan.id, label: planLabel(plan), action: actionLabel(plan) };
                  if (selected.action === 'Downgrade') setDowngrade(selected);
                  else setSubscription(selected);
                }}
              >
                {actionLabel(plan)}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="app-shell plans-page">
      <header className="topbar">
        <Brand href={user ? '/' : '/plans'} onClick={(event) => { if (onBack && !event.metaKey && !event.ctrlKey && !event.shiftKey && event.button === 0) { event.preventDefault(); onBack(); } }} />
        <HeaderNav user={user} onWorkspace={onBack} onLogout={onLogout} onOpenProfile={onOpenProfile} />
      </header>
      <main className="workspace hub-workspace">
        {user && <SubscriptionActivation key={`${user.user_id || user.username}:${paymentComplete}`} automatic={Boolean(paymentComplete)} onRefresh={onRefresh} />}
        <section className="workspace-intro">
          <div><p className="eyebrow">Access</p><h1>Plans.</h1></div>
          <p className="intro-copy">Compare monthly First Look allocations, Deep Dive selections, and hub access.</p>
        </section>
        {(user?.is_client || user?.plan === 'client') && <p className="plans-change-notice">Your shared client companies stay included when you upgrade. Your chosen tier provides your personal First Look and Deep Dive allowances in addition to those shared companies.</p>}
        {error && <div className="error-box workspace-error" role="alert">{error}</div>}
        {user && checkoutError && <div className="error-box workspace-error" role="alert">{checkoutError}</div>}
        {subscriptionStatusError && <div className="error-box workspace-error" role="alert">Unable to verify your current subscription: {subscriptionStatusError} Reload this page to retry.</div>}
        {pendingChange && <p className="plans-change-notice" role="status">Downgrade scheduled for {new Date(pendingChange.effective_at * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric' })} (America/New_York). Your current plan remains active until then.</p>}
        {!loading && !error && <section className="panel plans-roster-note" aria-labelledby="plans-roster-title">
          <h2 id="plans-roster-title">Research coverage and plan access</h2>
          <p>Our research covers over 400 companies across many sectors, but not the entire market. Depending on your tier, you can select First Look reports and Deep Dive reports from this roster of companies.</p>
          {plans.find((plan) => plan.id === 'tier_0')?.free_ticker && <p>Want to see what a First Look and Deep Dive report look like? Sign up for the free tier to read our full {plans.find((plan) => plan.id === 'tier_0').free_ticker} report.</p>}
          <p>Some tiers also include access to high-level information on all companies in our roster, shown as &ldquo;Full Roster&rdquo; below.</p>
          <p>All plans include every Deep Dive tab for companies you can access. Report Version History is available only to Tier 3.</p>
          <h3>How First Look and Deep Dive selections work</h3>
          <p>Your monthly First Look allowance is for First Look-only companies. Every active Deep Dive includes that company&apos;s First Look for free, so it does not use a First Look selection. You can choose a Deep Dive even when your First Look allowance is full.</p>
          <p>If you already selected a company&apos;s First Look this month, choosing its Deep Dive immediately returns that First Look selection to your monthly allowance. For example, if you have used 7 of 7 First Look selections, upgrading one of those companies to Deep Dive leaves 6 of 7 used and 1 available.</p>
          <p>This also applies to included Deep Dives and active credit unlocks. If Deep Dive access ends, an existing First Look selection for that company in the current month counts again. Removing a First Look alone does not return a selection.</p>
          {hasPaidPlan && <><h3>Upgrading your paid plan</h3><p>When you upgrade to a higher paid tier, you receive that tier&apos;s full First Look and Deep Dive allowances for new selections. All reports you already selected stay available until the end of the current calendar month and do not count against your new allowances. Those retained reports expire at month-end; selections made after the upgrade follow the new tier&apos;s normal rules. Reports included with your plan remain included.</p></>}
        </section>}
        {loading ? <div className="empty-state">Loading plans...</div> : (
          <div className="plans-compare" style={{ '--plan-count': Math.max(plans.length, 1) }}>
            <div className="plans-row plans-header"><div>Feature</div>{plans.map((plan) => <div className={cellClass(plan)} key={plan.id}><strong>{planLabel(plan)}</strong>{plan.id === currentPlan && <span className="plan-current-badge">Current plan</span>}</div>)}</div>
            {renderPriceRow()}
            {plans.some((plan) => plan.free_company_ticker) && <div className="plans-row"><div><strong>Free company</strong><p className="plan-hub-description">First Look and Deep Dive included on every tier without using your selections.</p></div>{plans.map((plan) => <div className={cellClass(plan)} key={`${plan.id}-free-company`}>{plan.free_company_ticker || '—'}</div>)}</div>}
            {PLAN_LIMIT_ROWS.filter(([key]) => key === 'company_reports_per_month' || plans.some((plan) => planLimit(plan, key) !== undefined)).map(([key, label]) => <div className="plans-row" key={key}><div><strong>{label}</strong><p className="plan-hub-description">{FEATURE_DESCRIPTIONS[key]}</p></div>{plans.map((plan) => <div className={cellClass(plan)} key={`${plan.id}-${key}`}>{planLimitLabel(plan, key)}</div>)}</div>)}
            {hubRows.length > 0 && <div className="plans-row plans-section"><div>Hub access</div>{plans.map((plan) => <div className={cellClass(plan)} key={`${plan.id}-hubs`} />)}</div>}
            {hubRows.map((hub) => <div className="plans-row" key={hub.id}><div><strong>{hub.label || hub.id}</strong>{HUB_DESCRIPTIONS[hub.id] && <p className="plan-hub-description">{HUB_DESCRIPTIONS[hub.id]}</p>}</div>{plans.map((plan) => <div className={cellClass(plan)} key={`${plan.id}-${hub.id}`}>{hubAccessLabel(plan, hub.id)}</div>)}</div>)}
            {renderPriceRow()}
          </div>
        )}
        {!loading && !error && <section className="panel plans-refresh-note" aria-labelledby="plans-refresh-title">
          <h2 id="plans-refresh-title">How often is the data refreshed?</h2>
          <p>Our production refresh schedule varies by dataset. All times below are Eastern time (America/New_York), with daylight saving time handled automatically.</p>
          <ul>
            <li><strong>Market Pulse and Market Cap:</strong> Weekday snapshots are scheduled hourly from 9:45 a.m. through 3:45 p.m., plus an after-close refresh at 4:45 p.m.</li>
            <li><strong>Latest News:</strong> A daily refresh starts at 7:00 p.m., including weekends.</li>
            <li><strong>Earnings Calendar:</strong> A daily refresh starts at 6:00 a.m., including weekends. Estimated earnings dates are not company-confirmed announcements.</li>
            <li><strong>Independent Director Buys:</strong> A daily refresh starts at 7:00 a.m., including weekends. Insider Trading updates depend on available SEC filings; shared hubs are also scheduled for publication at 4:45 p.m. on weekdays.</li>
            <li><strong>Pharma Landscape:</strong> A daily refresh starts at 8:00 p.m., including weekends, for companies included in the landscape. Publication time does not mean every underlying clinical source has changed.</li>
            <li><strong>Company research reports:</strong> A weekday scan starts at 4:00 a.m. to check for new filings, earnings materials, and other refresh signals. Report updates are triggered as needed; First Look and Deep Dive reports are not rewritten in full every day.</li>
          </ul>
          <p>These are scheduled job start times, not guaranteed publication times. Processing, source availability, and retries can delay updates. Weekday market schedules are not exchange-calendar-aware, so holidays and early closes may show an older quote date. Check each page&apos;s published, updated, or as-of timestamp and source dates. Data is not real-time, and opening a page does not trigger a new refresh.</p>
        </section>}
        {pricesError && <div className="error-box workspace-error" role="alert">Unable to load plan prices: {pricesError}</div>}
        {subscription && <StripeCheckoutModal plan={subscription} signedIn={Boolean(user)} onClose={() => setSubscription(null)} onComplete={completeCheckout} />}
        {downgrade && <PlanDowngradeDialog plan={downgrade} onClose={() => setDowngrade(null)} onScheduled={result => { setDowngrade(null); setPendingChange({ ...result, target: result.plan }); }} />}
      </main>
    </div>
  );
};

export default PlansPage;
